/**
 * Sidebar — the primary navigation of the authenticated shell.
 *
 * The item list is a single exported `navSections` constant so the topbar (for the
 * current page label) and the tests read exactly what is rendered. Visibility is
 * capability-driven: an item declares the `Permission` it needs and is filtered out
 * when `can()` says no — the UI never compares role strings.
 *
 * One instance renders both presentations: a fixed rail from `lg` up and a
 * slide-over below it. The slide-over borrows `useDismissable` from Modal so
 * Escape, the focus trap, focus restore and the scroll lock behave exactly like
 * every other overlay in the kit. The theme ships a right-hand slide keyframe
 * only, so this left-anchored panel fades in rather than inventing an
 * off-config animation (the same decision Drawer makes).
 */
import { useMemo, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  type LucideIcon,
  Activity,
  Award,
  BarChart3,
  BookMarked,
  BookOpen,
  Brain,
  Building2,
  Calculator,
  ClipboardCheck,
  ClipboardList,
  Code2,
  FileBarChart,
  FileCheck2,
  FolderKanban,
  GraduationCap,
  History,
  LayoutDashboard,
  ListChecks,
  Receipt,
  Rocket,
  Route as RouteIcon,
  Settings,
  ShieldCheck,
  SkipForward,
  Sparkles,
  TrendingUp,
  Users,
  UserCog,
  Wallet,
  X,
} from 'lucide-react';
import { Permission } from '@aforium/shared';
import { useAuth } from '../../providers/AuthProvider';
import { useDismissable } from '../ui/Modal';
import { cn } from '../../lib/cn';

/* -------------------------------------------------------------------------- */
/* The navigation model                                                       */
/* -------------------------------------------------------------------------- */

/** Which shell a section belongs to. A user sees exactly one of the two. */
export type NavAudience = 'intern' | 'staff';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Hide the item unless the signed-in user holds this capability. */
  permission?: Permission;
  /** Match the path exactly — needed for index routes and parents of sub-routes. */
  end?: boolean;
}

export interface NavSection {
  /** Stable key, also used to build the `aria-labelledby` id of the group. */
  id: string;
  title: string;
  audience: NavAudience;
  items: NavItem[];
}

const P = Permission;

/**
 * Every navigable destination in the product, grouped the way the sidebar shows
 * them. Exported so the topbar and tests share one source of truth.
 */
export const navSections: NavSection[] = [
  /* ---------------------------------------------------------------- intern */
  {
    id: 'learn',
    title: 'Learn',
    audience: 'intern',
    items: [
      { label: 'Dashboard', to: '/', icon: LayoutDashboard, end: true },
      { label: 'My Courses', to: '/courses', icon: BookOpen, permission: P.COURSE_VIEW },
      { label: 'My Progress', to: '/progress', icon: TrendingUp },
      { label: 'Learning History', to: '/history', icon: History },
    ],
  },
  {
    id: 'practice',
    title: 'Practice',
    audience: 'intern',
    items: [
      { label: 'Exercises', to: '/exercises', icon: Code2, permission: P.EXERCISE_SUBMIT },
      { label: 'My Submissions', to: '/submissions', icon: FileCheck2, permission: P.EXERCISE_SUBMIT },
      { label: 'Assessments', to: '/assessments', icon: ClipboardList, permission: P.ASSESSMENT_ATTEMPT },
    ],
  },
  {
    id: 'growth',
    title: 'Growth',
    audience: 'intern',
    items: [
      { label: 'My Skills', to: '/skills', icon: Brain },
      { label: 'Achievements', to: '/achievements', icon: Award },
      { label: 'Project Readiness', to: '/readiness', icon: Rocket },
    ],
  },
  {
    id: 'work',
    title: 'Work',
    audience: 'intern',
    items: [
      { label: 'My Project', to: '/projects', icon: FolderKanban, permission: P.PROJECT_VIEW },
      { label: 'My Tasks', to: '/tasks', icon: ListChecks, permission: P.TASK_SUBMIT },
    ],
  },
  {
    id: 'reference',
    title: 'Reference',
    audience: 'intern',
    items: [{ label: 'Engineering Handbook', to: '/handbook', icon: BookMarked }],
  },

  /* ----------------------------------------------------------------- staff */
  {
    id: 'overview',
    title: 'Overview',
    audience: 'staff',
    items: [
      { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, end: true },
      { label: 'Interns', to: '/admin/interns', icon: Users, permission: P.INTERN_VIEW },
    ],
  },
  {
    id: 'content',
    title: 'Content',
    audience: 'staff',
    items: [
      { label: 'Courses', to: '/admin/courses', icon: BookOpen, permission: P.COURSE_UPDATE },
      { label: 'Learning Paths', to: '/admin/learning-paths', icon: RouteIcon, permission: P.COURSE_UPDATE },
      { label: 'Assessments', to: '/admin/assessments', icon: ClipboardList, permission: P.COURSE_UPDATE },
      { label: 'Skills', to: '/admin/skills', icon: Sparkles, permission: P.SKILL_EVALUATE },
    ],
  },
  {
    id: 'review',
    title: 'Review',
    audience: 'staff',
    items: [
      { label: 'Review Queue', to: '/admin/submissions', icon: ClipboardCheck, permission: P.SUBMISSION_REVIEW },
      { label: 'Task Reviews', to: '/admin/task-submissions', icon: FileCheck2, permission: P.TASK_REVIEW },
      { label: 'Skip Requests', to: '/admin/skips', icon: SkipForward, permission: P.INTERN_OVERRIDE_PROGRESS },
    ],
  },
  {
    id: 'insight',
    title: 'Insight',
    audience: 'staff',
    items: [
      { label: 'Reports', to: '/admin/reports', icon: FileBarChart, permission: P.REPORT_VIEW },
      { label: 'Analytics', to: '/admin/analytics', icon: BarChart3, permission: P.ANALYTICS_VIEW },
      { label: 'Activity Log', to: '/admin/activity', icon: Activity, permission: P.ACTIVITY_LOG_VIEW },
      { label: 'Audit Log', to: '/admin/audit-logs', icon: ShieldCheck, permission: P.AUDIT_LOG_VIEW },
    ],
  },
  {
    id: 'projects',
    title: 'Projects',
    audience: 'staff',
    items: [
      { label: 'Projects', to: '/admin/projects', icon: FolderKanban, permission: P.PROJECT_VIEW_ALL },
      { label: 'Tasks', to: '/admin/tasks', icon: ListChecks, permission: P.TASK_MANAGE },
    ],
  },
  {
    id: 'business',
    title: 'Business',
    audience: 'staff',
    items: [
      { label: 'Clients', to: '/admin/clients', icon: Building2, permission: P.CLIENT_MANAGE },
      // `end` keeps Finance from staying active on its own sub-routes below.
      { label: 'Finance', to: '/admin/finance', icon: Wallet, permission: P.FINANCE_VIEW, end: true },
      { label: 'Invoices', to: '/admin/finance/invoices', icon: Receipt, permission: P.INVOICE_MANAGE },
      { label: 'Accountant', to: '/admin/finance/accountant', icon: Calculator, permission: P.FINANCE_VIEW },
    ],
  },
  {
    id: 'platform',
    title: 'Platform',
    audience: 'staff',
    items: [
      { label: 'Users', to: '/admin/users', icon: UserCog, permission: P.USER_MANAGE },
      { label: 'Settings', to: '/admin/settings', icon: Settings, permission: P.SETTINGS_MANAGE },
    ],
  },
];

export interface VisibleNavOptions {
  isStaff: boolean;
  can: (permission: Permission) => boolean;
}

/**
 * The sections this user may actually see: their audience only, items they hold
 * the capability for, and no empty groups left behind.
 */
export function getVisibleNavSections({ isStaff, can }: VisibleNavOptions): NavSection[] {
  const audience: NavAudience = isStaff ? 'staff' : 'intern';

  return navSections
    .filter((section) => section.audience === audience)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.permission || can(item.permission)),
    }))
    .filter((section) => section.items.length > 0);
}

/** True when `pathname` is this item's route, or a page nested beneath it. */
function matchesPath(item: NavItem, pathname: string): boolean {
  if (item.to === pathname) return true;
  if (item.end) return false;
  return pathname.startsWith(`${item.to === '/' ? '' : item.to}/`);
}

/**
 * The most specific nav item for a path — the topbar uses it to label the
 * current page on screens where the sidebar is hidden.
 */
export function findNavItem(pathname: string): NavItem | undefined {
  let best: NavItem | undefined;

  for (const section of navSections) {
    for (const item of section.items) {
      if (!matchesPath(item, pathname)) continue;
      if (!best || item.to.length > best.to.length) best = item;
    }
  }

  return best;
}

/* -------------------------------------------------------------------------- */
/* Presentation                                                               */
/* -------------------------------------------------------------------------- */

function Brand({ to, onNavigate }: { to: string; onNavigate?: () => void }) {
  return (
    <div className="flex h-16 shrink-0 items-center border-b border-border px-5">
      <Link
        to={to}
        onClick={onNavigate}
        className="flex items-center gap-2.5 rounded-lg py-1 pr-2 transition-opacity hover:opacity-90"
      >
        <span
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-xs"
          aria-hidden="true"
        >
          <GraduationCap className="h-4 w-4" />
        </span>
        <span className="flex flex-col leading-none">
          <span className="text-sm font-semibold tracking-tight text-content">Aforium</span>
          <span className="mt-1 text-2xs font-medium uppercase tracking-[0.16em] text-content-subtle">
            Learning
          </span>
        </span>
        <span className="sr-only">Aforium Learning — go to dashboard</span>
      </Link>
    </div>
  );
}

interface NavTreeProps {
  sections: NavSection[];
  /** Namespaces the group heading ids — the tree is rendered twice per page. */
  idPrefix: string;
  onNavigate?: () => void;
}

function NavTree({ sections, idPrefix, onNavigate }: NavTreeProps) {
  return (
    <nav aria-label="Main" className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-3 py-4">
      <ul className="flex flex-col gap-6">
        {sections.map((section) => {
          const headingId = `${idPrefix}-nav-${section.id}`;

          return (
            <li key={section.id}>
              <h2
                id={headingId}
                className="px-3 pb-2 text-2xs font-semibold uppercase tracking-wider text-content-subtle"
              >
                {section.title}
              </h2>

              <ul aria-labelledby={headingId} className="flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;

                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          cn(
                            'group relative flex h-9 items-center gap-3 rounded-lg pl-4 pr-3 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-primary-soft text-primary-on-soft'
                              : 'text-content-muted hover:bg-surface-sunken hover:text-content',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span
                              aria-hidden="true"
                              className={cn(
                                'absolute inset-y-1.5 left-0 w-1 rounded-full bg-primary transition-opacity',
                                isActive ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <Icon
                              className={cn(
                                'h-4 w-4 shrink-0 transition-colors',
                                isActive
                                  ? 'text-primary'
                                  : 'text-content-subtle group-hover:text-content-muted',
                              )}
                              aria-hidden="true"
                            />
                            <span className="truncate">{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export interface SidebarProps {
  /** Controls the slide-over below `lg`; the fixed rail ignores it. */
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { can, isStaff } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);

  const sections = useMemo(() => getVisibleNavSections({ isStaff, can }), [isStaff, can]);
  const home = isStaff ? '/admin' : '/';

  // Escape, focus trap, focus restore and the scroll lock, shared with every overlay.
  useDismissable({ isOpen, onClose, ref: panelRef, closeOnOutsideClick: false });

  return (
    <>
      {/* The permanent rail from `lg` up. */}
      <aside
        aria-label="Primary"
        className="hidden border-r border-border bg-surface lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:w-64 lg:flex-col"
      >
        <Brand to={home} />
        <NavTree sections={sections} idPrefix="rail" />
      </aside>

      {/* The slide-over below `lg`. */}
      {isOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-overlay/60 animate-fade-in"
            onClick={onClose}
            aria-hidden="true"
          />

          <div
            ref={panelRef}
            id="app-sidebar"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            tabIndex={-1}
            className="relative flex h-full w-72 max-w-[85vw] flex-col border-r border-border bg-surface shadow-popover animate-fade-in focus-visible:outline-none"
          >
            <Brand to={home} onNavigate={onClose} />

            {/* `data-autofocus` is what useDismissable focuses first on open. */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close navigation"
              data-autofocus
              className="absolute right-3 top-3.5 flex h-9 w-9 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-sunken hover:text-content"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>

            <NavTree sections={sections} idPrefix="drawer" onNavigate={onClose} />
          </div>
        </div>
      ) : null}
    </>
  );
}
